import type { ReactNode } from 'react';
import { tv } from 'tailwind-variants';

import { focusRing } from '../utils';

export const buttonStyles = tv({
  extend: focusRing,
  base: [
    'flex items-center justify-center gap-1.5',
    'cursor-pointer transition-[background-color,color,box-shadow] duration-150 motion-reduce:transition-none',
    'border-0 rounded-sm',
    'text-sm font-medium text-center whitespace-nowrap leading-none tabular-nums',
    '[&_svg]:shrink-0',
  ],
  variants: {
    color: {
      primary: 'bg-primary-700 hover:bg-primary-800 pressed:bg-primary-800 text-primary-contrast',
      secondary: 'bg-secondary-200 hover:bg-secondary-300 pressed:bg-secondary-400 text-secondary-800',
      destructive: 'bg-red-700 hover:bg-red-800 pressed:bg-red-900 text-white',
      positive: 'bg-green-700 hover:bg-green-800 pressed:bg-green-900 text-white',
    },
    variant: {
      solid: 'shadow-xs',
      soft: '',
      outline: '',
      ghost: '',
    },
    size: {
      'sm': 'min-h-7 px-2.5 [&_svg]:size-3.5',
      'md': 'min-h-9 px-3 [&_svg]:size-4',
      'icon-md': 'min-h-9 min-w-9 p-1.5 [&_svg]:size-5',
      'icon-sm': 'min-h-7 min-w-7 p-1 [&_svg]:size-4',
    },
    isDisabled: {
      true: 'cursor-default shadow-none',
    },
    isPending: {
      true: 'cursor-wait',
    },
    isActive: {
      true: '',
    },
  },
  compoundVariants: [
    {
      color: 'primary',
      variant: 'soft',
      class: [
        'bg-primary-100 hover:bg-primary-200 pressed:bg-primary-300 ',
        'text-primary-950',
        'inset-ring inset-ring-primary-200',
      ],
    },
    {
      color: 'primary',
      variant: 'outline',
      class: [
        'bg-transparent hover:bg-primary-200 pressed:bg-primary-300',
        'text-primary-950',
        'inset-ring inset-ring-primary-700',
      ],
    },
    {
      color: 'primary',
      variant: 'ghost',
      class: [
        'bg-transparent hover:bg-primary-200 pressed:bg-primary-300',
        'text-primary-800',
      ],
    },
    {
      isActive: true,
      color: 'primary',
      variant: 'solid',
      class: 'bg-primary-800 hover:bg-primary-800 text-primary-contrast',
    },
    {
      isActive: true,
      color: 'primary',
      variant: ['soft', 'outline', 'ghost'],
      class: 'bg-primary-200 hover:bg-primary-200 text-primary-950',
    },
    {
      color: 'secondary',
      variant: 'soft',
      class: [
        'bg-secondary-200 hover:bg-secondary-300 pressed:bg-secondary-400',
        'text-secondary-800',
        'inset-ring inset-ring-secondary-300',
      ],
    },
    {
      color: 'secondary',
      variant: 'ghost',
      class: [
        'bg-transparent hover:bg-secondary-200 pressed:bg-secondary-300',
        'text-secondary-600 hover:text-secondary-950 pressed:text-secondary-800',
      ],
    },
    {
      color: 'secondary',
      // Keep existing outline call sites on the same neutral surface as the
      // default secondary button. Soft remains the filled alternative.
      variant: ['solid', 'outline'],
      class: [
        'bg-primary-contrast hover:bg-secondary-100 pressed:bg-secondary-200',
        'text-secondary-800',
        'inset-ring inset-ring-secondary-300 hover:inset-ring-secondary-400',
        'shadow-xs pressed:shadow-none',
      ],
    },
    {
      isActive: true,
      color: 'secondary',
      variant: ['solid', 'soft', 'outline'],
      class: 'bg-secondary-200 hover:bg-secondary-200 text-secondary-950 inset-ring inset-ring-secondary-400 hover:inset-ring-secondary-400 shadow-none',
    },
    {
      isActive: true,
      color: 'secondary',
      variant: 'ghost',
      class: 'bg-secondary-200 hover:bg-secondary-200 text-secondary-950',
    },
    {
      color: 'destructive',
      variant: 'soft',
      class: [
        'bg-red-100 hover:bg-red-200 pressed:bg-red-300',
        'text-red-800',
        'inset-ring inset-ring-red-200',
      ],
    },
    {
      color: 'destructive',
      variant: 'ghost',
      class: [
        'bg-transparent hover:bg-red-100 pressed:bg-red-200',
        'text-red-700 hover:text-red-800',
      ],
    },
    {
      color: 'destructive',
      variant: 'outline',
      class: [
        'bg-transparent hover:bg-red-100 pressed:bg-red-200',
        'text-red-800',
        'inset-ring inset-ring-red-300',
      ],
    },
    {
      color: 'positive',
      variant: 'soft',
      class: 'bg-green-100 hover:bg-green-200 pressed:bg-green-300 text-green-800 inset-ring inset-ring-green-200',
    },
    {
      color: 'positive',
      variant: 'outline',
      class: 'bg-transparent hover:bg-green-100 pressed:bg-green-200 text-green-800 inset-ring inset-ring-green-300',
    },
    {
      color: 'positive',
      variant: 'ghost',
      class: 'bg-transparent hover:bg-green-100 pressed:bg-green-200 text-green-700',
    },
    // State overrides come last so no colour or active variant can make a
    // disabled control look actionable. This also covers disabled links.
    {
      isDisabled: true,
      variant: ['solid', 'soft', 'outline'],
      class: 'bg-secondary-100 hover:bg-secondary-100 pressed:bg-secondary-100 text-secondary-400 hover:text-secondary-400 pressed:text-secondary-400 inset-ring-secondary-200 hover:inset-ring-secondary-200 shadow-none forced-colors:text-[GrayText]',
    },
    {
      isDisabled: true,
      variant: 'ghost',
      class: 'bg-transparent hover:bg-transparent pressed:bg-transparent text-secondary-400 hover:text-secondary-400 pressed:text-secondary-400 inset-ring-0 shadow-none forced-colors:text-[GrayText]',
    },
  ],
  defaultVariants: {
    color: 'primary',
    size: 'md',
    variant: 'solid',
  },
});

type Styles = typeof buttonStyles['variants'];

export type ButtonBaseProps = {
  color?: keyof Styles['color'];
  variant?: keyof Styles['variant'];
  size?: keyof Styles['size'];
  icon?: { component: ReactNode };
  isActive?: boolean | undefined;
};
